import { PopoverSize, Select } from 'app/shared/components/generic-ui';
import { useRouteParams } from 'app/shared/hooks/useRouteParams';
import {
  CellAlignment,
  CellSize,
} from 'app/shared/components/generic-ui/Table/Table.types';
import { useCallback, useEffect, useState } from 'react';
import { useStyles } from './EntitiesDropdown.styles';
import { CustomTypography } from 'app/shared/components/generic-ui/Typography/Typography';
import { IBatchesCheckbox, IGetTicketProducts } from 'types';

import { useBatchHistoryList } from 'app/shared/hooks';

interface IEntitiesDropdownProps {
  label: string;
  selectedValues?: string[];
  selectedItems?: IBatchesCheckbox[];
  onChangeSelectedEntities: (
    values: string[],
    items?: IBatchesCheckbox[],
  ) => void;
}

export const TicketProductDropdown = ({
  label,
  selectedItems,
  selectedValues,
  onChangeSelectedEntities,
}: IEntitiesDropdownProps) => {
  const { classes } = useStyles();
  const [searchQuery, setSearchQuery] = useState('');
  const { organizationId }: { organizationId: string } = useRouteParams();

  const columns = [
    {
      id: 'externalId',
      label: 'ID',
      alignment: CellAlignment.LEFT,
      size: CellSize.MEDIUM,
    },
    {
      id: 'name',
      label: 'Name',
      alignment: CellAlignment.LEFT,
      size: CellSize.MEDIUM,
    },
  ];

  const {
    ticketProducts,
    hasMoreTicketProducts,
    fetchTicketProducts,
    isFetchingTicketProducts,
  } = useBatchHistoryList({
    organizationId,
  });

  const renderCell = useCallback((rowData: any, cellId: string) => {
    return <div style={{ whiteSpace: 'nowrap' }}>{rowData[cellId]}</div>;
  }, []);

  useEffect(() => {
    if (ticketProducts.length || searchQuery) {
      fetchTicketProducts({
        query: searchQuery,
        take: 50,
        page: 0,
      });
    }
  }, [fetchTicketProducts, searchQuery]);

  const loadMoreHandler = useCallback(() => {
    fetchTicketProducts({
      query: searchQuery,
      take: 50,
      page: Math.floor(ticketProducts.length / 50),
    });
  }, [fetchTicketProducts, searchQuery, ticketProducts]);

  const changeSelectedValuesHandler = useCallback(
    (values: string[]) => {
      const currentItems = values.map((idWithPrefix: string) => {
        const prefix = idWithPrefix[0];
        const id = idWithPrefix.slice(1);
        const element = ticketProducts.find(el => el.externalId === id);
        const selectedElement = selectedItems?.find(el => el.id === id);

        return element
          ? {
              id: element?.externalId || '',
              label: element?.name || '',
              prefix,
            }
          : { id: '', label: '', ...selectedElement, prefix };
      });

      onChangeSelectedEntities(values, currentItems);
    },
    [onChangeSelectedEntities, ticketProducts, selectedItems],
  );

  return (
    <div className={classes.selectContainer}>
      <CustomTypography variant="caption1" color="greyscale1">
        {label}
      </CustomTypography>

      <Select.Search
        searchQuery={searchQuery}
        isLoading={isFetchingTicketProducts}
        valueId={'externalId'}
        labelId={'name'}
        options={ticketProducts}
        selectedValues={selectedValues?.map(el => {
          const item = selectedItems?.find(i => el.includes(i?.label));
          return el.split(item?.label ?? '').join(item?.id);
        })}
        selectedItems={selectedItems}
        onChangeSelectedValues={changeSelectedValuesHandler}
        onSearchHandler={value => {
          setSearchQuery(value);
        }}
        loadMoreHandler={loadMoreHandler}
        showLoader={hasMoreTicketProducts}
        popoverSize={PopoverSize.BIG}
        renderCell={renderCell}
        columns={columns}
        clickHandler={() => {
          fetchTicketProducts({
            query: searchQuery,
            take: 50,
            page: 0,
          });
        }}
      />
    </div>
  );
};
